import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';
import { stihlMenuAnimate, stihlMenuItemAnimate } from '../helpers/animations';
import { motion } from 'framer-motion';
import { cx } from '@emotion/css';
import styles from '../navigation.module.scss';
import { useContentful } from '../hooks/useContentful';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useNavigation } from '../context/navigation-context-provider';
import { groupBy } from 'lodash';
import { AppSwitcherLink } from '../types/navigation.types';
import Skeleton from 'react-loading-skeleton';
import { getCurrentPlatform, getDesignSystemIconWithFallback, getUrlForPlatform } from '../helpers/utils';
import { FC, useEffect, useRef, useCallback, RefObject } from 'react';
import { DSHeading, DSText } from '@stihl-design-system/components';
import { cn } from '@phoenix/design-system/frontend/util/classNameUtils';

export const AppSwitcher: FC<{ isOpen?: boolean; onClose?: () => void; triggerRef?: RefObject<HTMLButtonElement> }> = ({
  isOpen = true,
  onClose,
  triggerRef,
}) => {
  const { t, i18n } = useTranslation(['shared']);
  const { getAppSwitcherNavItems, getAppSwitcherMetaNavItems } = useContentful();
  const { config } = useNavigation();
  const { md } = useBreakpoints();

  const appSwitcherItemsQuery = useQuery(['STIHL_NAV', 'APP_SWITCHER', i18n.language], () => getAppSwitcherNavItems(config.locale), {
    enabled: !!config.locale,
  });
  const appSwitcherMetaNavItemsQuery = useQuery(
    ['STIHL_NAV', 'APP_SWITCHER', 'META_ITEMS', i18n.language],
    () => getAppSwitcherMetaNavItems(config.locale),
    {
      enabled: !!config.locale,
    }
  );

  const groupedApps = Object.entries(groupBy(appSwitcherItemsQuery?.data, 'category'))
    // Sort by category
    ?.sort(([categoryA]) => (categoryA.toLowerCase() === 'web' ? -1 : 0))
    // Hide mobile section on desktop
    ?.filter(([category]) => (md ? category.toLowerCase() !== 'app' : true));

  const filterMobileLinks = (links: AppSwitcherLink[], category: string) => {
    return links?.filter((app) => {
      if (category?.toLowerCase() === 'web') {
        return true;
      }

      const platform = getCurrentPlatform();

      if (platform === 'android') {
        return !!app.urlAndroid;
      }

      if (platform === 'ios') {
        return !!app.urlIos;
      }

      return true;
    });
  };

  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  // Use a ref to track if the first menu item ref has been set across renders
  const firstMenuItemSetRef = useRef(false);

  // Reset the tracker before rendering menu items
  firstMenuItemSetRef.current = false;

  useEffect(() => {
    if (isOpen && appSwitcherItemsQuery.isSuccess && firstMenuItemRef.current) {
      setTimeout(() => {
        firstMenuItemRef.current?.focus();
      }, 0);
    }
  }, [isOpen, appSwitcherItemsQuery.isSuccess]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (!onClose) {
      return;
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose, handleKeyDown]);

  // Restore focus to trigger button when menu closes
  useEffect(() => {
    if (!isOpen && triggerRef?.current) {
      triggerRef.current.focus();
    }
  }, [isOpen, triggerRef]);

  return (
    <motion.div
      initial="exit"
      variants={stihlMenuAnimate}
      animate={isOpen ? 'enter' : 'exit'}
      className="z-10 cursor-default bg-transparent text-left md:absolute md:inset-auto md:right-0 md:top-9 md:w-80"
      data-testid="navigation_appswitcher"
      hidden={!isOpen}
      aria-labelledby="app-switcher-button"
      role="menu"
    >
      <div
        className="mt-2 flex flex-col bg-primary-white text-primary-black md:shadow-md"
        aria-busy={appSwitcherItemsQuery?.isLoading ? 'true' : undefined}
        aria-live="polite"
      >
        <motion.div
          variants={stihlMenuItemAnimate}
          className="pb-2 stihl-text-body-lg-bold md:border-b md:border-secondary md:p-4 md:stihl-text-body-default-bold"
        >
          {t('topBar_meta_apps_headline')}
        </motion.div>

        {appSwitcherItemsQuery?.isLoading && (
          <div className="mt-4 flex flex-col gap-4 md:mt-0 md:p-8">
            <div className="flex items-center gap-4">
              <Skeleton circle={true} className="h-6 w-6" />
              <Skeleton className="h-4 w-52" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton circle={true} className="h-6 w-6" />
              <Skeleton className="h-4 w-52" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton circle={true} className="h-6 w-6" />
              <Skeleton className="h-4 w-52" />
            </div>
          </div>
        )}

        {appSwitcherItemsQuery?.isSuccess && appSwitcherItemsQuery?.data && appSwitcherItemsQuery?.data?.length === 0 && (
          <DSText size="small" className="p-8">
            {t('topBar_meta_apps_emptyState')}
          </DSText>
        )}

        {appSwitcherItemsQuery?.data && appSwitcherItemsQuery?.data?.length > 0 && (
          <div className="flex flex-col bg-primary-white pt-0 stihl-text-body-sm-reg">
            {groupedApps?.map(([category, apps]) => (
              <nav aria-labelledby={`app-switcher-${category}-heading`} className="pb-4" key={category}>
                <DSHeading size="small" id={`app-switcher-${category}-heading`} className="pb-2 pt-4 px-4">
                  {t(`topBar_meta_apps_subhead_${category.toLowerCase()}`)}
                </DSHeading>
                <ul className="flex flex-col">
                  {filterMobileLinks(apps, category)?.map((app) => {
                    let ref;
                    if (!firstMenuItemSetRef.current) {
                      ref = firstMenuItemRef;
                      firstMenuItemSetRef.current = true;
                    }
                    return (
                      <motion.li variants={stihlMenuItemAnimate} key={app.id} role="none">
                        <a
                          href={getUrlForPlatform(app, category)}
                          className={cx(
                            'flex items-center gap-2 py-2 px-4',
                            'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline',
                            styles.stihlIdMenuItem
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...(ref ? { ref } : {})}
                        >
                          <img src={app.iconUrl?.replace(/^\/\//, 'https://')} alt={`${app.displayName} Icon`} className="block h-5 w-5" />
                          <span>{app.displayName}</span>
                          <span className="sr-only">{t('shared:shared_a11y_hint_opensInNewTab')}</span>
                        </a>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>
            ))}

            <hr className="border-secondary mx-4" />

            {appSwitcherMetaNavItemsQuery?.data?.map((item) => {
              return (
                <a
                  href={item.url}
                  className={cn(
                    'flex items-center gap-2 p-4 stihl-text-body-sm-bold',
                    'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline'
                  )}
                  key={item.url}
                  data-testid="navigation_appswitcher_link_element"
                  rel="noopener"
                >
                  <svg className="inline-block h-3 w-2 fill-current">
                    <use xlinkHref={getDesignSystemIconWithFallback('icon-arrow-right-16.svg') + '#img'} />
                  </svg>
                  <span>{item.name}</span>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};
